import React, { useEffect, useState, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import Mailbox from '../components/Mailbox';
import CarsatPreparation from '../components/CarsatPreparation';
import { openScanModal } from '../components/ScanModal';
import { STATUTS_LIST, STATUS_CONFIG, STATUTS_CARSAT, MODELES } from '../utils/constants';
import { toast } from 'sonner';
import {
  FileText, Upload, Download, Trash2, Plus, Edit2, Save, X, ExternalLink,
  RefreshCw, Package, Globe, Mail, ChevronDown, DollarSign, FolderOpen, Search, ShoppingCart, AlertTriangle, Check, Copy, ClipboardCheck, Camera, XCircle
} from 'lucide-react';

const tabs = ['Informations', 'Emails', 'Documents', 'Commandes', 'Interventions', 'CARSAT', 'Finance'];

// Fonction pour nettoyer les numéros de téléphone (enlever points, espaces, tirets)
const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  return phone.replace(/[^0-9+]/g, '');
};

// Fonction pour copier dans le presse-papier (avec fallback)
const copyToClipboard = async (text) => {
  // Méthode moderne
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Clipboard API failed:', err);
    }
  }
  
  // Fallback pour les contextes non-sécurisés
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    document.execCommand('copy');
    textArea.remove();
    return true;
  } catch (err) {
    console.error('execCommand failed:', err);
    textArea.remove();
    return false;
  }
};

// Composant bouton copier
function CopyButton({ value, isPhone = false }) {
  if (!value || value === '—') return null;
  
  const handleCopy = async () => {
    const textToCopy = isPhone ? cleanPhoneNumber(value) : value;
    const success = await copyToClipboard(textToCopy);
    if (success) {
      toast.success('Copié !', { duration: 1500 });
    } else {
      toast.error('Erreur de copie');
    }
  };
  
  return (
    <button
      onClick={handleCopy}
      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
      title={isPhone ? `Copier: ${cleanPhoneNumber(value)}` : `Copier: ${value}`}
    >
      <Copy size={14} />
    </button>
  );
}

// Composant champ avec bouton copier
function CopyableField({ label, value, isPhone = false, className = "" }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-xs text-gray-500 w-24 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-900 flex-1">{value || '—'}</span>
      <CopyButton value={value} isPhone={isPhone} />
    </div>
  );
}

function TabBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${active ? 'border-[hsl(222,47%,11%)] text-[hsl(222,47%,11%)]' : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'}`}
      data-testid={`tab-${label.toLowerCase()}`}
    >
      {label}
    </button>
  );
}

export default function ClientFiche() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [commandes, setCommandes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('Informations');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCommandeId, setSelectedCommandeId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showNewCmdModal, setShowNewCmdModal] = useState(false);
  const [cmdLignes, setCmdLignes] = useState([{ produit_id: '', reference: '', designation: '', quantite: 1, prix_unitaire_ht: 0 }]);
  const [cmdFrais, setCmdFrais] = useState(780);
  const [cmdDate, setCmdDate] = useState(new Date().toISOString().split('T')[0]);
  const [cmdStatut, setCmdStatut] = useState('incomplet');
  const [produits, setProduits] = useState([]);
  const [creatingCmd, setCreatingCmd] = useState(false);
  const [deletingCmdId, setDeletingCmdId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [clientInterventions, setClientInterventions] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [changingCommercial, setChangingCommercial] = useState(false);

  // Calculs financiers depuis les lignes
  const calcCmd = (lignes, frais) => {
    const total_produits_ht = lignes.reduce((sum, l) => sum + (parseFloat(l.prix_unitaire_ht || 0) * parseInt(l.quantite || 1)), 0);
    const frais_ht = parseFloat(frais || 0);
    const total_ht = Math.round((total_produits_ht + frais_ht) * 100) / 100;
    const tva = Math.round(total_ht * 0.20 * 100) / 100;
    const total_ttc = Math.round((total_ht + tva) * 100) / 100;
    const subvention = Math.round(total_ht * 0.70 * 100) / 100;
    return { total_produits_ht: Math.round(total_produits_ht * 100) / 100, frais_ht, total_ht, tva, total_ttc, subvention };
  };

  const handleProduitSelect = (idx, produit_id) => {
    const prod = produits.find(p => p.id === produit_id);
    if (prod) {
      setCmdLignes(prev => prev.map((l, i) => i === idx ? { ...l, produit_id: prod.id, reference: prod.reference, designation: prod.designation, prix_unitaire_ht: prod.prix_ht } : l));
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [clientRes, commandesRes, docsRes, notesRes, interventionsRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/commandes?client_id=${id}`),
        api.get(`/documents/client/${id}`),
        api.get(`/clients/${id}/notes`).catch(() => ({ data: [] })),
        api.get(`/interventions/client/${id}`).catch(() => ({ data: [] }))
      ]);
      setClient(clientRes.data);
      setEditData(clientRes.data);
      setCommandes(commandesRes.data.commandes);
      setDocuments(docsRes.data);
      setNotes(notesRes.data || []);
      setClientInterventions(interventionsRes.data || []);
      if (commandesRes.data.commandes.length > 0 && !selectedCommandeId) {
        setSelectedCommandeId(commandesRes.data.commandes[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    api.get('/users/commerciaux/list').then(r => setCommerciaux(r.data || [])).catch(() => {});
  }, []);

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/clients/${id}/notes`, { content: newNote });
      toast.success('Note ajoutée');
      setNewNote('');
      const notesRes = await api.get(`/clients/${id}/notes`);
      setNotes(notesRes.data || []);
    } catch (e) { toast.error('Erreur'); }
    finally { setAddingNote(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/clients/${id}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note supprimée');
    } catch (e) { toast.error('Erreur'); }
  };

  const saveClient = async () => {
    try {
      await api.put(`/clients/${id}`, editData);
      toast.success('Client mis à jour');
      setEditing(false);
      fetchAll();
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const handleChangeCommercial = async (newCommercialId) => {
    setChangingCommercial(true);
    try {
      const cascade = commandes.length > 0 && window.confirm('Mettre aussi à jour le commercial sur toutes les commandes de ce client ?');
      const res = await api.put(`/clients/${id}/commercial`, { commercial_id: newCommercialId, cascade_commandes: cascade });
      toast.success(`Commercial mis à jour : ${res.data.commercial_prenom} ${res.data.commercial_nom}${cascade ? ` (${res.data.commandes_updated} commandes)` : ''}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setChangingCommercial(false); }
  };

  const handleUpload = async (file, typeDoc) => {
    if (!file) return;
    setUploading(true);
    
    // If client has Google Drive folder, upload there
    if (client?.google_drive_folder_id) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', typeDoc);
      try {
        await api.post(`/drive/upload/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Document uploadé sur Google Drive');
        fetchAll();
      } catch (e) { 
        toast.error(e.response?.data?.detail || 'Erreur upload Google Drive'); 
      }
      finally { setUploading(false); }
      return;
    }
    
    // Fallback to local storage
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', id);
    formData.append('type_doc', typeDoc);
    if (selectedCommandeId) formData.append('commande_id', selectedCommandeId);
    try {
      await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploadé');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur upload'); }
    finally { setUploading(false); }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await api.get(`/documents/${docId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement'); }
  };

  const downloadPDF = async (type, commandeId) => {
    try {
      const res = await api.get(`/pdf/${type}/${commandeId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${type}_${client?.societe?.toLowerCase().replace(/ /g,'_')}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF généré !');
    } catch { toast.error('Erreur génération PDF'); }
  };

  const deleteDoc = async docId => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document supprimé');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const openNewCmdModal = async () => {
    try {
      const res = await api.get('/produits');
      setProduits(res.data.filter(p => p.actif !== false));
    } catch {}
    setShowNewCmdModal(true);
  };

  const deleteClient = async () => {
    setDeleting(true);
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client supprimé avec succès');
      navigate('/clients');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const createCommande = async () => {
    const lignesValides = cmdLignes.filter(l => l.produit_id && l.quantite > 0);
    if (lignesValides.length === 0) { toast.error('Ajoutez au moins un produit'); return; }
    setCreatingCmd(true);
    try {
      const res = await api.post('/commandes', {
        client_id: id,
        lignes: lignesValides,
        frais_livraison: parseFloat(cmdFrais),
        statut: cmdStatut,
        date_commande: cmdDate ? new Date(cmdDate).toISOString() : new Date().toISOString()
      });
      toast.success(`Commande ${res.data.numero} créée`);
      setShowNewCmdModal(false);
      setCmdLignes([{ produit_id: '', reference: '', designation: '', quantite: 1, prix_unitaire_ht: 0 }]);
      setCmdFrais(780); setCmdDate(new Date().toISOString().split('T')[0]); setCmdStatut('incomplet');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally { setCreatingCmd(false); }
  };

  const deleteCommande = async (cmdId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette commande définitivement ?')) return;
    setDeletingCmdId(cmdId);
    try {
      await api.delete(`/commandes/${cmdId}`);
      toast.success('Commande supprimée');
      setCommandes(prev => prev.filter(c => c.id !== cmdId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingCmdId(null);
    }
  };

  if (loading) return <div className="p-8 flex items-center justify-center"><RefreshCw size={20} className="animate-spin text-gray-400" /></div>;
  if (!client) return <div className="p-8 text-center text-gray-500">Client introuvable</div>;

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white";
  const docTypes = [
    { key: 'rib', label: 'RIB', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: true },
    { key: 'urssaf', label: 'Attestation URSSAF', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: true },
    { key: 'attestation_fournisseur', label: 'Attestation fournisseur', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: true },
    { key: 'bon_commande', label: 'Bon de commande', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: true },
    { key: 'de_minimis', label: 'Attestation Minimis', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: true },
    { key: 'facture', label: 'Facture', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: true },
    { key: 'fiche_renseignement', label: 'Fiche de renseignement', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: false },
    { key: 'bon_livraison', label: 'Bon de livraison', accept: '.pdf,.jpg,.jpeg,.png,.webp', required: false },
  ];

  return (
    <div className="min-h-screen bg-[hsl(210,20%,98%)]">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-modal-overlay">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" data-testid="delete-modal">
            {/* Modal Header */}
            <div className="bg-red-500 text-white px-6 py-4 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <p className="font-bold text-lg">Supprimer ce client ?</p>
                  <p className="text-red-100 text-sm">Cette action est irréversible</p>
                </div>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">Client</p>
                <p className="font-bold text-xl text-gray-900">{client.societe}</p>
                {client.siret && <p className="text-sm text-gray-500 mt-1">SIRET: {client.siret}</p>}
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800">
                  <strong>Attention :</strong> Toutes les données associées seront perdues :
                </p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Informations du client</li>
                  <li>Commandes associées</li>
                  <li>Documents uploadés</li>
                  <li>Dossiers CARSAT</li>
                  <li>Données financières</li>
                </ul>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
                data-testid="cancel-delete-btn"
              >
                Annuler
              </button>
              <button
                onClick={deleteClient}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Commande Modal */}
      {showNewCmdModal && (() => {
        const calc = calcCmd(cmdLignes, cmdFrais);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-commande-modal-overlay">
            <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="new-commande-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                <h2 className="font-bold text-gray-900 flex items-center gap-2"><ShoppingCart size={18} />Nouvelle commande</h2>
                <button onClick={() => setShowNewCmdModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} className="text-gray-500" /></button>
              </div>

              <div className="p-6 space-y-5">
                {/* Lignes produits */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lignes de produits</label>
                    <button onClick={() => setCmdLignes(p => [...p, { produit_id: '', reference: '', designation: '', quantite: 1, prix_unitaire_ht: 0 }])} className="flex items-center gap-1 text-xs text-[hsl(222,47%,11%)] hover:underline font-medium">
                      <Plus size={12} /> Ajouter une ligne
                    </button>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs text-gray-500 font-medium">Produit</th>
                          <th className="text-center px-3 py-2 text-xs text-gray-500 font-medium w-20">Qté</th>
                          <th className="text-right px-3 py-2 text-xs text-gray-500 font-medium">Prix unit. HT</th>
                          <th className="text-right px-3 py-2 text-xs text-gray-500 font-medium">Sous-total</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {cmdLignes.map((ligne, idx) => {
                          const sous_total = (parseFloat(ligne.prix_unitaire_ht || 0) * parseInt(ligne.quantite || 1));
                          return (
                            <tr key={ligne.produit_id || `ligne-${idx}`}>
                              <td className="px-3 py-2">
                                <select
                                  value={ligne.produit_id}
                                  onChange={e => handleProduitSelect(idx, e.target.value)}
                                  className="w-full text-sm border-0 outline-none bg-transparent focus:ring-0"
                                  data-testid={`cmd-produit-${idx}`}
                                >
                                  <option value="">Choisir un produit...</option>
                                  {produits.map(p => <option key={p.id} value={p.id}>{p.reference} — {p.designation}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number" min="1"
                                  value={ligne.quantite}
                                  onChange={e => setCmdLignes(prev => prev.map((l, i) => i === idx ? { ...l, quantite: parseInt(e.target.value) || 1 } : l))}
                                  className="w-full text-center text-sm border-0 outline-none bg-transparent"
                                  data-testid={`cmd-qte-${idx}`}
                                />
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {ligne.prix_unitaire_ht > 0 ? `${parseFloat(ligne.prix_unitaire_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-gray-900">
                                {sous_total > 0 ? `${sous_total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : '—'}
                              </td>
                              <td className="px-2 py-2">
                                {cmdLignes.length > 1 && (
                                  <button onClick={() => setCmdLignes(p => p.filter((_, i) => i !== idx))} className="p-1 text-gray-300 hover:text-red-500 transition-colors">
                                    <X size={12} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Frais + Résumé */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Frais livraison HT (€)</label>
                    <input type="number" min="0" step="0.01" value={cmdFrais} onChange={e => setCmdFrais(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]" data-testid="new-cmd-frais" />
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm border border-gray-200">
                    <div className="flex justify-between text-gray-500"><span>Total produits HT</span><span>{calc.total_produits_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                    <div className="flex justify-between text-gray-500"><span>Frais HT</span><span>{calc.frais_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                    <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-1.5"><span>Total HT</span><span>{calc.total_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                    <div className="flex justify-between text-gray-500"><span>TVA 20%</span><span>{calc.tva.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                    <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1.5"><span>Total TTC</span><span>{calc.total_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                    <div className="flex justify-between text-emerald-700 font-medium bg-emerald-50 rounded px-2 py-1 mt-1"><span>CARSAT 70%</span><span>{calc.subvention.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                  </div>
                </div>

                {/* Date + Statut */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Date de commande</label>
                    <input type="date" value={cmdDate} onChange={e => setCmdDate(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]" data-testid="new-cmd-date" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Statut initial</label>
                    <select value={cmdStatut} onChange={e => setCmdStatut(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]" data-testid="new-cmd-statut">
                      {STATUTS_LIST.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
                <button onClick={() => setShowNewCmdModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 text-sm hover:bg-gray-50" data-testid="new-cmd-cancel">Annuler</button>
                <button onClick={createCommande} disabled={creatingCmd} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[hsl(222,47%,11%)] text-white rounded-xl text-sm font-medium hover:bg-[hsl(222,47%,18%)] disabled:opacity-50" data-testid="new-cmd-submit">
                  {creatingCmd ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                  {creatingCmd ? 'Création...' : 'Créer la commande'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Client header */}
      <div className="bg-white border-b border-[hsl(214,32%,91%)]">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[hsl(222,47%,11%)] rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{client.societe?.[0]?.toUpperCase()}</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)]" data-testid="client-name">{client.societe}</h1>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400 font-mono">{client.code_client}</span>
                    {client.siret && <span className="text-xs text-gray-500">SIRET: {client.siret}</span>}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {client.google_drive_folder_url && (
                <a href={client.google_drive_folder_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="google-drive-link">
                  <FolderOpen size={14} className="text-green-600" /> Google Drive
                </a>
              )}
              {editing ? (
                <>
                  <button onClick={saveClient} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700" data-testid="save-client-btn"><Save size={14} />Enregistrer</button>
                  <button onClick={() => { setEditing(false); setEditData(client); }} className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-lg text-sm hover:bg-gray-50" data-testid="cancel-edit-btn"><X size={14} />Annuler</button>
                </>
              ) : (
                <>
                  <button onClick={() => setEditing(true)} className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="edit-client-btn"><Edit2 size={14} />Modifier</button>
                  <button 
                    onClick={() => setShowDeleteModal(true)} 
                    className="flex items-center gap-2 border border-red-200 px-4 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors" 
                    data-testid="delete-client-btn"
                  >
                    <Trash2 size={14} />Supprimer
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-5 border-b border-gray-100 overflow-x-auto">
            {tabs.map(t => <TabBtn key={t} label={t} active={activeTab === t} onClick={() => setActiveTab(t)} />)}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* TAB: Informations */}
        {activeTab === 'Informations' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Informations générales</h3>
              <div className="space-y-3">
                {[
                  { label: 'Société', key: 'societe' },
                  { label: 'SIRET', key: 'siret' },
                  { label: 'Gérant', key: 'gerant' },
                  { label: 'Adresse', key: 'adresse' },
                  { label: 'Code postal', key: 'code_postal' },
                  { label: 'Ville', key: 'ville' },
                ].map(({ label, key }) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-24 flex-shrink-0">{label}</span>
                    {editing ? (
                      <input className={inputCls} value={editData[key] || ''} onChange={e => setEditData(p => ({...p, [key]: e.target.value}))} />
                    ) : (
                      <>
                        <span className="text-sm text-gray-900 flex-1">{client[key] || '—'}</span>
                        <CopyButton value={client[key]} />
                      </>
                    )}
                  </div>
                ))}
                {/* Commercial */}
                <div className="flex items-center gap-3 pt-3 mt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500 w-24 flex-shrink-0">Commercial</span>
                  {changingCommercial ? (
                    <RefreshCw size={14} className="animate-spin text-gray-400" />
                  ) : (
                    <select
                      value={client?.commercial_id || ''}
                      onChange={e => handleChangeCommercial(e.target.value)}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white"
                      data-testid="commercial-select"
                    >
                      <option value="">— Non assigné —</option>
                      {commerciaux.map(c => (
                        <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Contacts</h3>
              <div className="space-y-3">
                {[
                  { label: 'Email salon', key: 'email_salon', isPhone: false },
                  { label: 'Tél salon', key: 'tel_salon', isPhone: true },
                  { label: 'Tél portable', key: 'tel_portable', isPhone: true },
                  { label: 'Comptable', key: 'comptable_nom', isPhone: false },
                  { label: 'Email compta', key: 'comptable_email', isPhone: false },
                  { label: 'Tél compta', key: 'comptable_tel', isPhone: true },
                  { label: 'Médecine travail', key: 'medecine_travail', isPhone: false },
                ].map(({ label, key, isPhone }) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-24 flex-shrink-0">{label}</span>
                    {editing ? (
                      <input className={inputCls} value={editData[key] || ''} onChange={e => setEditData(p => ({...p, [key]: e.target.value}))} />
                    ) : (
                      <>
                        <span className="text-sm text-gray-900 flex-1">{client[key] || '—'}</span>
                        <CopyButton value={client[key]} isPhone={isPhone} />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Net Entreprises */}
            <div className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm">
              <h3 className="font-semibold text-amber-800 mb-4">Identifiants Net Entreprises</h3>
              <div className="space-y-2 text-sm">
                {['nom','prenom','email_dossier'].map(k => (
                  <div key={k} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-24">{k.replace('_',' ')}</span>
                    {editing ? (
                      <input className={inputCls} value={editData.identifiants_ne?.[k] || ''} onChange={e => setEditData(p => ({...p, identifiants_ne: {...(p.identifiants_ne||{}), [k]: e.target.value}}))} />
                    ) : (
                      <>
                        <span className="text-gray-900 flex-1">{client.identifiants_ne?.[k] || '—'}</span>
                        <CopyButton value={client.identifiants_ne?.[k]} />
                      </>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-24">mot de passe</span>
                  {editing ? (
                    <input type="text" className={inputCls} value={editData.identifiants_ne?.mot_de_passe || ''} onChange={e => setEditData(p => ({...p, identifiants_ne: {...(p.identifiants_ne||{}), mot_de_passe: e.target.value}}))} />
                  ) : (
                    <>
                      <span className="text-gray-900 font-mono flex-1">{client.identifiants_ne?.mot_de_passe || '—'}</span>
                      <CopyButton value={client.identifiants_ne?.mot_de_passe} />
                    </>
                  )}
                </div>
                {/* Statut Accès N.E */}
                <div className="flex flex-col gap-2 pt-2 mt-2 border-t border-amber-100">
                  {editing ? (
                    <div className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-500">Accès Net Entreprises</span>
                      {[
                        { value: 'codes_ok', label: 'Codes NE OK — accès fonctionnel', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
                        { value: 'anydesk', label: 'AnyDesk prévu avec le comptable', color: 'text-amber-700 bg-amber-50 border-amber-200' },
                        { value: 'non_resolu', label: 'Non résolu', color: 'text-red-700 bg-red-50 border-red-200' },
                      ].map(opt => (
                        <label key={opt.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${(editData.acces_ne_statut || 'non_resolu') === opt.value ? opt.color + ' ring-1 ring-offset-1' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <input
                            type="radio"
                            name="acces_ne_statut"
                            value={opt.value}
                            checked={(editData.acces_ne_statut || 'non_resolu') === opt.value}
                            onChange={e => setEditData(p => ({...p, acces_ne_statut: e.target.value}))}
                            className="w-3.5 h-3.5"
                            data-testid={`acces-ne-${opt.value}`}
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {client.acces_ne_statut === 'codes_ok' ? (
                        <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                          <Check size={14} /> Codes NE OK — accès fonctionnel
                        </span>
                      ) : client.acces_ne_statut === 'anydesk' ? (
                        <div>
                          <span className="flex items-center gap-1.5 text-amber-600 text-sm font-medium">
                            <AlertTriangle size={14} /> AnyDesk prévu avec le comptable
                          </span>
                          <p className="text-xs text-amber-500 ml-5 mt-0.5">RDV à prendre avec le comptable</p>
                        </div>
                      ) : (
                        <span className="flex items-center gap-1.5 text-red-600 text-sm font-medium">
                          <XCircle size={14} /> Accès NE non résolu — bloquant pour CARSAT
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Domain & Google Drive */}
            <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Globe size={16} /> Domaine & Services</h3>
              
              {/* Google Drive Section */}
              <div className="mb-4 pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><FolderOpen size={12} /> Google Drive</p>
                {editing ? (
                  <div className="space-y-2">
                    <input
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                      value={editData.google_drive_folder_url || ''}
                      onChange={e => setEditData(p => ({ ...p, google_drive_folder_url: e.target.value }))}
                      placeholder="https://drive.google.com/drive/folders/..."
                      data-testid="gdrive-url-input"
                    />
                    <input
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                      value={editData.google_drive_folder_id || ''}
                      onChange={e => setEditData(p => ({ ...p, google_drive_folder_id: e.target.value }))}
                      placeholder="ID du dossier Drive"
                      data-testid="gdrive-id-input"
                    />
                  </div>
                ) : client.google_drive_folder_id ? (
                  <a
                    href={client.google_drive_folder_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 transition-colors"
                    data-testid="open-gdrive-folder"
                  >
                    <FolderOpen size={14} />
                    Ouvrir le dossier Drive
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <GoogleDriveButton clientId={id} clientName={client.societe} onSuccess={fetchAll} />
                )}
              </div>
              
              {/* Domain Section */}
              <div>
                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><Globe size={12} /> Domaine .store & Email</p>
                {client.domaine ? (
                  <div className="space-y-3">
                    <div className="bg-emerald-50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-emerald-800">{client.domaine}</p>
                        {client.domaine_status !== 'active' && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await api.post(`/domains/sync-status/${id}`);
                                if (res.data.new_status !== res.data.previous_status) {
                                  toast.success(`Statut mis à jour: ${res.data.new_status}`);
                                  fetchAll();
                                } else {
                                  toast.info(`Statut inchangé: ${res.data.new_status}`);
                                }
                              } catch (e) {
                                toast.error('Erreur lors de la synchronisation');
                              }
                            }}
                            className="text-emerald-600 hover:text-emerald-800 p-1 rounded hover:bg-emerald-100"
                            title="Synchroniser le statut avec OVH"
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                      </div>
                      {client.domaine_status && (
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${client.domaine_status === 'active' ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-100 text-amber-700'}`}>
                          {client.domaine_status === 'active' ? 'actif' : client.domaine_status === 'pending' ? 'en attente' : client.domaine_status}
                        </span>
                      )}
                    </div>
                    {/* Email section */}
                    {client.email_domaine ? (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-xs text-blue-600 mb-1">Adresse email</p>
                        <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                          <Mail size={14} />
                          {client.email_domaine}
                        </p>
                        <button
                          onClick={() => setActiveTab('Emails')}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Ouvrir la boîte mail →
                        </button>
                      </div>
                    ) : (
                      <CreateEmailButton clientId={id} domainName={client.domaine} onSuccess={fetchAll} />
                    )}
                  </div>
                ) : (
                  <DomainSearchSection clientId={id} clientData={client} onSuccess={fetchAll} />
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="md:col-span-2 bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText size={18} className="text-gray-600" />
                  Notes
                </h3>
              </div>
              
              {/* Add note form */}
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  placeholder="Ajouter une note..."
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-gray-50"
                  onKeyDown={e => e.key === 'Enter' && addNote()}
                  data-testid="new-note-input"
                />
                <button
                  onClick={addNote}
                  disabled={addingNote || !newNote.trim()}
                  className="px-4 py-2.5 bg-[hsl(222,47%,11%)] text-white rounded-xl text-sm font-medium hover:bg-[hsl(222,47%,18%)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  data-testid="add-note-btn"
                >
                  <Plus size={16} />
                  Ajouter
                </button>
              </div>

              {/* Notes list */}
              <div className="space-y-3">
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-400 italic text-center py-4">Aucune note</p>
                ) : (
                  notes.map(note => (
                    <div key={note.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100 group" data-testid={`note-${note.id}`}>
                      <p className="text-sm text-gray-900 mb-2">{note.content}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-400">
                          📅 {new Date(note.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} — {note.author_name || 'Admin'}
                        </p>
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Supprimer"
                          data-testid={`delete-note-${note.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Emails */}
        {activeTab === 'Emails' && (
          <Mailbox 
            clientId={id} 
            emailAddress={client.email_domaine}
            emailPassword={client.email_domaine_password}
            emailServer={client.email_domaine_server}
            onEmailConfigured={(email, password, server) => {
              setClient(prev => ({
                ...prev,
                email_domaine: email,
                email_domaine_password: password,
                email_domaine_server: server,
                email_domaine_status: 'active'
              }));
            }}
          />
        )}

        {/* TAB: Documents */}
        {activeTab === 'Documents' && (
          <div className="space-y-5">
            {commandes.length > 1 && (
              <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-4 shadow-sm">
                <label className="text-sm font-medium text-gray-700 block mb-2">Commande associée</label>
                <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" value={selectedCommandeId || ''} onChange={e => setSelectedCommandeId(e.target.value)}>
                  {commandes.map(c => <option key={c.id} value={c.id}>{c.numero} — {c.modele}{c.lignes?.length ? '' : ` x${c.quantite}`}</option>)}
                </select>
              </div>
            )}

            {/* Document checklist */}
            <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Documents requis</h3>
              <p className="text-xs text-gray-500 mb-4">Les images (JPG, PNG) sont automatiquement converties en PDF</p>
              <div className="space-y-3">
                {docTypes.map(dtype => {
                  const uploaded = documents.filter(d => d.type_doc === dtype.key);
                  const driveDoc = uploaded.find(d => d.drive_file_url);
                  const isComplete = uploaded.length > 0;
                  return (
                    <div key={dtype.key} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border-2 transition-all ${isComplete ? 'border-emerald-200 bg-emerald-50' : dtype.required ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-emerald-500' : dtype.required ? 'bg-red-400' : 'bg-gray-300'}`}>
                          {isComplete ? (
                            <Check size={14} className="text-white" />
                          ) : (
                            <span className="text-white text-xs font-bold">{dtype.required ? '!' : '?'}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-gray-900">{dtype.label}</p>
                            {dtype.required && !isComplete && (
                              <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Obligatoire</span>
                            )}
                          </div>
                          {isComplete && driveDoc?.drive_file_url && (
                            <a 
                              href={driveDoc.drive_file_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                            >
                              <ExternalLink size={10} />
                              Voir sur Google Drive
                            </a>
                          )}
                          {isComplete && !driveDoc?.drive_file_url && (
                            <p className="text-xs text-emerald-600 truncate">{uploaded[0].filename_stored || 'Document uploadé'}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                        {isComplete && (
                          <>
                            {driveDoc?.drive_file_url ? (
                              <a 
                                href={driveDoc.drive_file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-1.5 rounded-lg bg-white border border-gray-200 hover:bg-blue-50 transition-colors"
                                data-testid={`view-${dtype.key}`}
                              >
                                <ExternalLink size={14} className="text-blue-600" />
                              </a>
                            ) : (
                              <button onClick={() => downloadDoc(uploaded[0].id, uploaded[0].filename_stored)} className="p-1.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors" data-testid={`download-${dtype.key}`}>
                                <Download size={14} className="text-gray-600" />
                              </button>
                            )}
                            <button onClick={() => deleteDoc(uploaded[0].id)} className="p-1.5 rounded-lg bg-white border border-gray-200 hover:bg-red-50 transition-colors" data-testid={`delete-${dtype.key}`}>
                              <Trash2 size={14} className="text-red-400" />
                            </button>
                          </>
                        )}
                        <div className="flex rounded-lg overflow-hidden border border-gray-200">
                          <label className={`cursor-pointer flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors border-r border-gray-200 ${isComplete ? 'bg-white text-gray-700 hover:bg-gray-50' : 'bg-[hsl(222,47%,11%)] text-white hover:bg-[hsl(222,47%,18%)]'}`} data-testid={`upload-${dtype.key}`}>
                            <Upload size={11} />
                            Fichier
                            <input type="file" className="hidden" accept={dtype.accept} onChange={e => handleUpload(e.target.files[0], dtype.key)} disabled={uploading} />
                          </label>
                          <button
                            onClick={(e) => { e.stopPropagation(); openScanModal({ docType: dtype.key, onComplete: (file) => handleUpload(file, dtype.key) }); }}
                            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${isComplete ? 'bg-white text-indigo-600 hover:bg-indigo-50' : 'bg-[hsl(222,47%,11%)] text-indigo-300 hover:bg-[hsl(222,47%,18%)]'}`}
                            data-testid={`scan-${dtype.key}`}
                          >
                            <Camera size={11} />
                            Scan
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Other documents */}
            {documents.filter(d => !['rib','urssaf','medecine_travail','bon_livraison','bon_commande','fiche_renseignement','de_minimis','attestation_fournisseur','facture'].includes(d.type_doc)).length > 0 && (
              <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Autres documents</h3>
                <div className="space-y-2">
                  {documents.filter(d => !['rib','urssaf','medecine_travail','bon_livraison','bon_commande','fiche_renseignement','de_minimis','attestation_fournisseur','facture'].includes(d.type_doc)).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-gray-400" />
                        <span className="text-sm text-gray-700">{doc.filename_stored}</span>
                        <span className="text-xs text-gray-400">{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <button onClick={() => downloadDoc(doc.id, doc.filename_stored)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-white">
                        <Download size={14} className="text-gray-600" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: Commandes */}
        {activeTab === 'Commandes' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={openNewCmdModal}
                className="flex items-center gap-2 bg-[hsl(222,47%,11%)] text-white px-4 py-2 rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] transition-colors"
                data-testid="new-commande-btn"
              >
                <Plus size={14} />Nouvelle commande
              </button>
            </div>
            {commandes.length === 0 ? (
              <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-12 text-center text-gray-400">
                <Package size={40} className="mx-auto mb-3 opacity-30" />
                <p>Aucune commande</p>
              </div>
            ) : (
              commandes.map(cmd => (
                <div key={cmd.id} className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center justify-between flex-wrap gap-3 cursor-pointer" onClick={() => navigate(`/commandes/${cmd.id}`)}>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-gray-500">{cmd.numero}</span>
                        <StatusBadge statut={cmd.statut} />
                      </div>
                      <p className="font-semibold text-gray-900 mt-1">{cmd.modele}{cmd.lignes?.length ? '' : ` x${cmd.quantite}`}</p>
                      <p className="text-xs text-gray-400">{new Date(cmd.date_commande).toLocaleDateString('fr-FR')}</p>
                    </div>
                    {/* Résumé financier */}
                    {cmd.total_ht > 0 && (
                      <div className="flex gap-4 text-sm text-right">
                        <div>
                          <p className="text-xs text-gray-400">Total HT</p>
                          <p className="font-semibold text-gray-900">{cmd.total_ht?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">TVA</p>
                          <p className="font-medium text-gray-600">{cmd.tva?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">TTC</p>
                          <p className="font-bold text-gray-900">{cmd.total_ttc?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg px-3 py-1">
                          <p className="text-xs text-emerald-600">CARSAT 70%</p>
                          <p className="font-bold text-emerald-700">{(cmd.total_ht * 0.7).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={e => { e.stopPropagation(); downloadPDF('facture', cmd.id); }} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50" data-testid={`dl-facture-${cmd.id}`}>
                        <Download size={12} /> Facture
                      </button>
                      <button onClick={e => { e.stopPropagation(); downloadPDF('attestation-fournisseur', cmd.id); }} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50" data-testid={`dl-attestation-${cmd.id}`}>
                        <FileText size={12} /> Attestation
                      </button>
                      <button onClick={e => { e.stopPropagation(); downloadPDF('de-minimis', cmd.id); }} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50" data-testid={`dl-de-minimis-${cmd.id}`}>
                        <FileText size={12} /> De Minimis
                      </button>
                      <button
                        onClick={e => deleteCommande(cmd.id, e)}
                        disabled={deletingCmdId === cmd.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-500 rounded-lg text-xs hover:bg-red-50 transition-colors disabled:opacity-50"
                        data-testid={`delete-commande-${cmd.id}`}
                      >
                        {deletingCmdId === cmd.id ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* TAB: Interventions */}
        {activeTab === 'Interventions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Historique des interventions ({clientInterventions.length})</h3>
            </div>
            {clientInterventions.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">Aucune intervention pour ce client</p>
              </div>
            ) : (
              <div className="space-y-3">
                {clientInterventions.map(iv => {
                  const sConfig = { a_planifier: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'À planifier' }, planifiee: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Planifiée' }, en_cours: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'En cours' }, terminee: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Terminée' }, annulee: { bg: 'bg-red-100', text: 'text-red-600', label: 'Annulée' } };
                  const sc = sConfig[iv.statut] || sConfig.a_planifier;
                  const datePart = iv.date_prevue ? iv.date_prevue.substring(0, 10).split('-').reverse().join('/') : '—';
                  return (
                    <div key={iv.id} onClick={() => navigate(`/interventions/${iv.id}`)} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md cursor-pointer transition-shadow" data-testid={`client-intervention-${iv.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${iv.priorite === 'urgente' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                            {iv.type?.toUpperCase() || 'SAV'} {iv.priorite === 'urgente' ? '- Urgente' : ''}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${sc.bg} ${sc.text}`}>{sc.label}</span>
                        </div>
                        <span className="text-xs text-gray-400">{datePart}</span>
                      </div>
                      <p className="font-medium text-gray-900">{iv.titre}</p>
                      {iv.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{iv.description}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        {iv.technicien_nom && <span>Technicien: {iv.technicien_nom}</span>}
                        <span>{iv.notes_terrain?.length || 0} note{(iv.notes_terrain?.length || 0) > 1 ? 's' : ''}</span>
                        <span>{iv.photo_count || 0} photo{(iv.photo_count || 0) > 1 ? 's' : ''}</span>
                      </div>
                      {iv.statut === 'terminee' && iv.compte_rendu && (
                        <div className="mt-2 p-2 bg-emerald-50 rounded-lg text-xs text-emerald-800">
                          <span className="font-medium">Compte-rendu:</span> {iv.compte_rendu}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB: CARSAT */}
        {activeTab === 'CARSAT' && <CarsatTab clientId={id} commandes={commandes} />}

        {/* TAB: Finance */}
        {activeTab === 'Finance' && <FinanceTab clientId={id} commandes={commandes} />}
      </div>
    </div>
  );
}

function CarsatTab({ clientId, commandes }) {
  const [dossiers, setDossiers] = useState([]);
  const [form, setForm] = useState({ commande_id: '', numero_dossier: '', date_demande: '', date_envoi: '', statut: 'en_attente', montant_subvention: 0 });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [preparationId, setPreparationId] = useState(null);

  const reload = () => api.get(`/carsat?client_id=${clientId}`).then(r => setDossiers(r.data)).catch(() => {});

  useEffect(() => {
    reload();
    setLoading(false);
  }, [clientId]);

  // Pré-remplir subvention à 70% du total HT de la commande sélectionnée
  const handleCommandeChange = (commande_id) => {
    const cmd = commandes.find(c => c.id === commande_id);
    const total_ht = cmd?.total_ht || 0;
    const subvention = Math.round(total_ht * 0.70 * 100) / 100;
    setForm(p => ({ ...p, commande_id, montant_subvention: subvention }));
  };

  const submit = async () => {
    try {
      await api.post('/carsat', { ...form, client_id: clientId });
      toast.success('Dossier CARSAT créé');
      setShowForm(false);
      setForm({ commande_id: '', numero_dossier: '', date_demande: '', date_envoi: '', statut: 'en_attente', montant_subvention: 0 });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const updateField = async (dossierId, field, value) => {
    try {
      await api.put(`/carsat/${dossierId}`, { [field]: value });
      setDossiers(prev => prev.map(d => d.id === dossierId ? { ...d, [field]: value } : d));
    } catch { toast.error('Erreur de mise à jour'); }
  };

  const deleteDossier = async (dossierId) => {
    if (!window.confirm('Supprimer ce dossier CARSAT définitivement ?')) return;
    setDeletingId(dossierId);
    try {
      await api.delete(`/carsat/${dossierId}`);
      toast.success('Dossier supprimé');
      setDossiers(prev => prev.filter(d => d.id !== dossierId));
    } catch { toast.error('Erreur'); }
    finally { setDeletingId(null); }
  };

  const inputCls = "w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white";

  if (loading) return <div className="p-8 flex justify-center"><RefreshCw size={20} className="animate-spin text-gray-400" /></div>;

  // Show preparation view if a dossier is selected
  if (preparationId) {
    return (
      <CarsatPreparation
        dossierId={preparationId}
        onClose={() => { setPreparationId(null); reload(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[hsl(222,47%,11%)] text-white px-4 py-2 rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] transition-colors" data-testid="new-carsat-btn">
          <Plus size={14} />Nouveau dossier CARSAT
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Nouveau dossier CARSAT / Subvention</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Commande liée</label>
              <select className={inputCls} value={form.commande_id} onChange={e => handleCommandeChange(e.target.value)} data-testid="carsat-commande-select">
                <option value="">Sélectionner...</option>
                {commandes.map(c => <option key={c.id} value={c.id}>{c.numero} — {c.modele}{c.lignes?.length ? '' : ` x${c.quantite}`}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">N° Dossier</label>
              <input className={inputCls} value={form.numero_dossier} onChange={e => setForm(p => ({ ...p, numero_dossier: e.target.value }))} placeholder="102743" data-testid="carsat-numero-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date de demande</label>
              <input type="date" className={inputCls} value={form.date_demande} onChange={e => setForm(p => ({ ...p, date_demande: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date d'envoi dossier</label>
              <input type="date" className={inputCls} value={form.date_envoi} onChange={e => setForm(p => ({ ...p, date_envoi: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Montant subvention (€) <span className="text-emerald-600">70% TH auto</span></label>
              <input type="number" className={inputCls} value={form.montant_subvention} onChange={e => setForm(p => ({ ...p, montant_subvention: parseFloat(e.target.value) || 0 }))} data-testid="carsat-montant-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Statut initial</label>
              <select className={inputCls} value={form.statut} onChange={e => setForm(p => ({ ...p, statut: e.target.value }))}>
                {STATUTS_CARSAT.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={submit} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700" data-testid="save-carsat-btn">Créer le dossier</button>
            <button onClick={() => setShowForm(false)} className="border border-gray-200 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
          </div>
        </div>
      )}

      {dossiers.length === 0 && !showForm && (
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-12 text-center text-gray-400">
          <p>Aucun dossier CARSAT</p>
        </div>
      )}

      {dossiers.map(d => (
        <div key={d.id} className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-semibold text-gray-900">Dossier CARSAT</p>
              <p className="text-xs text-gray-400">{commandes.find(c => c.id === d.commande_id)?.numero || d.commande_id}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${d.statut === 'accepte' || d.statut === 'subvention_recue' ? 'bg-emerald-50 text-emerald-700' : d.statut === 'refuse' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                {STATUTS_CARSAT.find(s => s.value === d.statut)?.label || d.statut}
              </span>
              <button onClick={() => deleteDossier(d.id)} disabled={deletingId === d.id} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Supprimer" data-testid={`delete-carsat-${d.id}`}>
                {deletingId === d.id ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">N° Dossier</label>
              <input className={inputCls} defaultValue={d.numero_dossier || ''} onBlur={e => e.target.value !== d.numero_dossier && updateField(d.id, 'numero_dossier', e.target.value)} placeholder="102743" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date de demande</label>
              <input type="date" className={inputCls} defaultValue={d.date_demande?.split('T')[0] || ''} onBlur={e => updateField(d.id, 'date_demande', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date d'envoi</label>
              <input type="date" className={inputCls} defaultValue={d.date_envoi?.split('T')[0] || ''} onBlur={e => updateField(d.id, 'date_envoi', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Statut</label>
              <select className={inputCls} value={d.statut} onChange={e => updateField(d.id, 'statut', e.target.value)}>
                {STATUTS_CARSAT.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Subvention reçue le</label>
              <input type="date" className={inputCls} defaultValue={d.date_reception_subvention?.split('T')[0] || ''} onBlur={e => updateField(d.id, 'date_reception_subvention', e.target.value)} />
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <label className="text-xs text-emerald-600 block mb-1">Montant subvention (€)</label>
              <input type="number" className="w-full text-lg font-bold text-emerald-700 bg-transparent border-none outline-none focus:outline-none" defaultValue={d.montant_subvention || 0} onBlur={e => updateField(d.id, 'montant_subvention', parseFloat(e.target.value) || 0)} data-testid={`carsat-montant-${d.id}`} />
            </div>
          </div>

          {/* Bouton préparation dossier */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => setPreparationId(d.id)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm font-medium hover:bg-[hsl(222,47%,18%)] transition-colors w-full justify-center"
              data-testid={`prepare-dossier-${d.id}`}
            >
              <ClipboardCheck size={16} />
              Préparer le dossier
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function FinanceTab({ clientId, commandes }) {
  const [selectedCmd, setSelectedCmd] = useState(commandes[0]?.id || null);
  const [finance, setFinance] = useState(null);
  const [form, setForm] = useState({ facture_vente: { numero: '', montant_ht: 0, montant_tva: 0, montant_ttc: 0 }, facture_achat: { montant_ht: 0, montant_tva: 0, montant_ttc: 0 } });
  const [regForm, setRegForm] = useState({ montant: 0, date: new Date().toISOString().split('T')[0], mode: 'virement' });
  const [loading, setLoading] = useState(false);

  const fetchFinance = useCallback(async (cmdId) => {
    if (!cmdId) return;
    setLoading(true);
    try {
      const r = await api.get(`/finance/commande/${cmdId}`);
      setFinance(r.data);
      if (r.data) {
        setForm({ facture_vente: r.data.facture_vente || {}, facture_achat: r.data.facture_achat || {} });
      }
    } catch { setFinance(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedCmd) fetchFinance(selectedCmd); }, [selectedCmd, fetchFinance]);

  const saveFinance = async () => {
    const cmd = commandes.find(c => c.id === selectedCmd);
    try {
      await api.post('/finance', { commande_id: selectedCmd, client_id: clientId, facture_vente: form.facture_vente, facture_achat: form.facture_achat });
      toast.success('Finance enregistrée');
      fetchFinance(selectedCmd);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const addReglement = async () => {
    try {
      await api.post(`/finance/commande/${selectedCmd}/reglement`, regForm);
      toast.success('Règlement ajouté');
      fetchFinance(selectedCmd);
      setRegForm({ montant: 0, date: new Date().toISOString().split('T')[0], mode: 'virement' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white";

  return (
    <div className="space-y-4">
      {commandes.length > 1 && (
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-4 shadow-sm">
          <label className="text-sm font-medium text-gray-700 block mb-2">Commande</label>
          <select className={inputCls} value={selectedCmd || ''} onChange={e => setSelectedCmd(e.target.value)}>
            {commandes.map(c => <option key={c.id} value={c.id}>{c.numero} — {c.modele}</option>)}
          </select>
        </div>
      )}

      {loading ? <div className="p-8 flex justify-center"><RefreshCw size={20} className="animate-spin text-gray-400" /></div> : (
        <>
          {/* Factures */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><DollarSign size={14} className="text-blue-600" />Facture de vente</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">N° Facture</label>
                  <input className={inputCls} value={form.facture_vente?.numero || ''} onChange={e => setForm(p=>({...p,facture_vente:{...p.facture_vente,numero:e.target.value}}))} placeholder="FA26551131" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {['montant_ht','montant_tva','montant_ttc'].map(k => (
                    <div key={k}>
                      <label className="text-xs text-gray-500 block mb-1">{k.replace('montant_','').toUpperCase()}</label>
                      <input type="number" className={inputCls} value={form.facture_vente?.[k] || 0} onChange={e => setForm(p=>({...p,facture_vente:{...p.facture_vente,[k]:parseFloat(e.target.value)||0}}))} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Facture d'achat</h3>
              <div className="grid grid-cols-3 gap-2">
                {['montant_ht','montant_tva','montant_ttc'].map(k => (
                  <div key={k}>
                    <label className="text-xs text-gray-500 block mb-1">{k.replace('montant_','').toUpperCase()}</label>
                    <input type="number" className={inputCls} value={form.facture_achat?.[k] || 0} onChange={e => setForm(p=>({...p,facture_achat:{...p.facture_achat,[k]:parseFloat(e.target.value)||0}}))} />
                  </div>
                ))}
              </div>
              <button onClick={saveFinance} className="mt-4 w-full bg-[hsl(222,47%,11%)] text-white px-4 py-2 rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] transition-colors" data-testid="save-finance-btn">
                Enregistrer
              </button>
            </div>
          </div>

          {/* Règlements */}
          {finance && (
            <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Règlements reçus</h3>
              <div className="space-y-2 mb-4">
                {(finance.reglements || []).map((r, i) => (
                  <div key={`regl-${r.date}-${r.montant}-${i}`} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-emerald-700">{r.montant?.toLocaleString('fr-FR')} €</span>
                      <span className="text-xs text-gray-500">{r.mode}</span>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(r.date).toLocaleDateString('fr-FR')}</span>
                  </div>
                ))}
                {(finance.reglements || []).length === 0 && <p className="text-sm text-gray-400">Aucun règlement</p>}
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Ajouter un règlement</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Montant (€)</label>
                    <input type="number" className={inputCls} value={regForm.montant} onChange={e=>setRegForm(p=>({...p,montant:parseFloat(e.target.value)||0}))} data-testid="reglement-montant-input" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Date</label>
                    <input type="date" className={inputCls} value={regForm.date} onChange={e=>setRegForm(p=>({...p,date:e.target.value}))} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Mode</label>
                    <select className={inputCls} value={regForm.mode} onChange={e=>setRegForm(p=>({...p,mode:e.target.value}))}>
                      {['virement','cheque','especes','prelevement'].map(m=><option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <button onClick={addReglement} className="mt-3 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700" data-testid="add-reglement-btn">
                  Ajouter le règlement
                </button>
              </div>

              {/* Balance */}
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Total facture</p>
                  <p className="font-bold text-gray-900">{finance.facture_vente?.montant_ttc?.toLocaleString('fr-FR') || 0} €</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-emerald-600">Total réglé</p>
                  <p className="font-bold text-emerald-700">{finance.total_regle?.toLocaleString('fr-FR') || 0} €</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${finance.solde_du > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                  <p className={`text-xs ${finance.solde_du > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Solde dû</p>
                  <p className={`font-bold text-lg ${finance.solde_du > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="solde-du">{finance.solde_du?.toLocaleString('fr-FR') || 0} €</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Google Drive Button Component
function GoogleDriveButton({ clientId, clientName, onSuccess }) {
  const [loading, setLoading] = useState(false);
  
  const createFolder = async () => {
    setLoading(true);
    try {
      const res = await api.post('/drive/folder', {
        client_id: clientId,
        client_name: clientName
      });
      toast.success('Dossier Google Drive créé !');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création du dossier');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <button
      onClick={createFolder}
      disabled={loading}
      className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors disabled:opacity-50"
      data-testid="create-gdrive-folder-btn"
    >
      {loading ? (
        <RefreshCw size={14} className="animate-spin" />
      ) : (
        <FolderOpen size={14} />
      )}
      Créer le dossier Drive
    </button>
  );
}

// Domain Search Section Component
function DomainSearchSection({ clientId, clientData, onSuccess }) {
  const [domainName, setDomainName] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [registering, setRegistering] = useState(false);
  
  const searchDomain = async () => {
    if (!domainName.trim()) {
      toast.error('Entrez un nom de domaine');
      return;
    }
    setSearching(true);
    setSearchResult(null);
    try {
      const res = await api.post('/domains/search', { domain_name: domainName.toLowerCase().trim() });
      setSearchResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };
  
  const registerDomain = async () => {
    if (!searchResult?.available) return;
    setRegistering(true);
    try {
      const res = await api.post('/domains/register', {
        client_id: clientId,
        domain_name: domainName.toLowerCase().trim(),
        owner_first_name: clientData.gerant?.split(' ')[0] || 'Gérant',
        owner_last_name: clientData.gerant?.split(' ').slice(1).join(' ') || clientData.societe,
        owner_email: clientData.email_salon || 'contact@ibnservices.fr',
        owner_phone: clientData.tel_salon || clientData.tel_portable || '+33100000000',
        owner_address: clientData.adresse || 'Adresse',
        owner_city: clientData.ville || 'Ville',
        owner_zip: clientData.code_postal || '75000',
        owner_country: 'FR'
      });
      toast.success(`Domaine ${res.data.domain_name} commandé ! (${res.data.total_price}€)`);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setRegistering(false);
    }
  };
  
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={domainName}
            onChange={e => setDomainName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="nom-du-salon"
            className="w-full px-3 py-2 pr-16 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
            data-testid="domain-search-input"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">.store</span>
        </div>
        <button
          onClick={searchDomain}
          disabled={searching || !domainName.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] disabled:opacity-50 transition-colors"
          data-testid="domain-search-btn"
        >
          {searching ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
          Vérifier
        </button>
      </div>
      
      {searchResult && (
        <div className={`p-3 rounded-lg ${searchResult.available ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${searchResult.available ? 'text-emerald-800' : 'text-red-800'}`}>
                {searchResult.domain}
              </p>
              <p className={`text-xs ${searchResult.available ? 'text-emerald-600' : 'text-red-600'}`}>
                {searchResult.available ? `Disponible - ${searchResult.price}€/an` : 'Non disponible'}
              </p>
            </div>
            {searchResult.available && (
              <button
                onClick={registerDomain}
                disabled={registering}
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                data-testid="domain-register-btn"
              >
                {registering ? <RefreshCw size={12} className="animate-spin" /> : <ShoppingCart size={12} />}
                Acheter
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Create Email Button Component
function CreateEmailButton({ clientId, domainName, onSuccess }) {
  const [loading, setLoading] = useState(false);
  
  const createEmail = async () => {
    setLoading(true);
    try {
      const res = await api.post('/domains/email/create', {
        client_id: clientId,
        domain_name: domainName,
        account_name: 'contact',
        password: process.env.REACT_APP_OVH_DEFAULT_EMAIL_PASSWORD || ''
      });
      
      if (res.data.status === 'created') {
        toast.success(`Email ${res.data.email_address} créé !`);
      } else if (res.data.status === 'already_exists') {
        toast.info(res.data.message || 'Ce compte email existe déjà');
      } else {
        toast.warning(res.data.message || 'Email en attente d\'activation');
      }
      
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création de l\'email');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <button
      onClick={createEmail}
      disabled={loading}
      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors disabled:opacity-50 border border-blue-200"
      data-testid="create-email-btn"
    >
      {loading ? (
        <RefreshCw size={14} className="animate-spin" />
      ) : (
        <Mail size={14} />
      )}
      {loading ? 'Création...' : 'Créer contact@' + domainName}
    </button>
  );
}

// End of file
